import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl } from 'react-native';
import { Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { useToast } from '../../src/components/Toast';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

interface TabRow {
  id: string;
  locationName: string;
  customerName: string;
  customerEmail: string;
  status: string;
  uncollected: boolean;
  mode: string;
  totalCents: number;
  itemCount: number;
  failureCount: number;
  lastError: string | null;
  nextRetryAt: string | null;
  openedAt: string;
  lastActivityAt: string;
  settledAt: string | null;
  paymentBrand: string | null;
  paymentLast4: string | null;
}

const FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'uncollected', label: 'Uncollected' },
  { value: 'settled', label: 'Settled' },
] as const;

/** Owner view of visit tabs (combined-charge visits). Customers never see tabs. */
export default function OwnerVisitTabsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const [filter, setFilter] = useState<'open' | 'uncollected' | 'settled'>('uncollected');
  const [rows, setRows] = useState<TabRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState<string | null>(null);

  const fetchTabs = useCallback(async (f: string) => {
    try {
      const res = await api<{ rows: TabRow[] }>(`/api/owner/visit-tabs?status=${f}`);
      setRows(res.rows);
    } catch (err: any) {
      if (!isOffline) toast.show({ type: 'error', title: 'Failed to load visit tabs', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [isOffline]);

  useEffect(() => { setLoading(true); fetchTabs(filter); }, [filter, fetchTabs]);

  const settle = async (id: string) => {
    setSettling(id);
    try {
      const res = await api<{ ok: boolean; status: string; lastError: string | null }>(
        `/api/owner/visit-tabs/${id}/settle`, { method: 'POST' });
      if (res.status === 'settled') {
        toast.show({ type: 'success', title: 'Charged', message: 'The visit was charged successfully.' });
      } else {
        toast.show({ type: 'error', title: 'Charge failed', message: res.lastError || 'The card was declined. It will retry automatically.' });
      }
      fetchTabs(filter);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Charge failed', message: err.message });
    } finally {
      setSettling(null);
    }
  };

  const fmtWhen = (iso: string | null) => iso
    ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <>
      <Stack.Screen options={{ title: 'Visit Billing' }} />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scroll}
        refreshControl={<RefreshControl refreshing={false} onRefresh={() => fetchTabs(filter)} />}
      >
        <ContentContainer>
          <Text style={styles.intro}>
            Combined-charge visits. Customers pay per selection; the visit is charged once when it closes. Uncollected = the card declined at close (retries automatically; that card is blocked for new purchases until it collects — other payment methods still work, and a successful payment on another card retries the balance).
          </Text>
          <View style={styles.chips}>
            {FILTERS.map(f => (
              <TouchableOpacity
                key={f.value}
                style={[styles.chip, filter === f.value && styles.chipActive]}
                onPress={() => setFilter(f.value)}
              >
                <Text style={[styles.chipText, filter === f.value && styles.chipTextActive]}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {loading ? (
            <Text style={styles.empty}>Loading…</Text>
          ) : rows.length === 0 ? (
            <Text style={styles.empty}>No {filter} visit tabs.</Text>
          ) : rows.map(tab => (
            <View key={tab.id} style={[styles.card, tab.uncollected && styles.cardWarn]}>
              <View style={styles.cardHeader}>
                <Text style={styles.customer}>{tab.customerName}</Text>
                <Text style={styles.amount}>${(tab.totalCents / 100).toFixed(2)}</Text>
              </View>
              <Text style={styles.meta}>{tab.customerEmail}</Text>
              <Text style={styles.meta}>
                {tab.locationName} · {tab.itemCount} item{tab.itemCount === 1 ? '' : 's'} · opened {fmtWhen(tab.openedAt)}
              </Text>
              {tab.status === 'settled' && (
                <Text style={styles.meta}>
                  Settled {fmtWhen(tab.settledAt)}{tab.paymentBrand ? ` · ${tab.paymentBrand} ····${tab.paymentLast4}` : ''}
                </Text>
              )}
              {tab.uncollected && (
                <View style={styles.errorBox}>
                  <Ionicons name="warning" size={14} color={colors.danger} />
                  <Text style={styles.errorText}>
                    {tab.failureCount} failed attempt{tab.failureCount === 1 ? '' : 's'}
                    {tab.lastError ? ` — ${tab.lastError}` : ''}
                    {tab.nextRetryAt ? ` · retries ${fmtWhen(tab.nextRetryAt)}` : ''}
                  </Text>
                </View>
              )}
              {tab.status === 'open' && (
                <Button
                  title={settling === tab.id ? 'Charging…' : 'Charge now'}
                  variant="outline"
                  icon={<Ionicons name="card" size={18} color={colors.primary} />}
                  onPress={() => settle(tab.id)}
                  disabled={settling !== null}
                  style={styles.settleBtn}
                />
              )}
            </View>
          ))}
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const useStyles = (colors: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  scroll: { padding: spacing.md },
  intro: { fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md, lineHeight: 18 },
  chips: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  chip: {
    paddingHorizontal: spacing.md, paddingVertical: spacing.xs,
    borderRadius: borderRadius.full, borderWidth: 1, borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: fontSize.sm, color: colors.text },
  chipTextActive: { color: colors.white, fontWeight: fontWeight.semibold },
  empty: { fontSize: fontSize.sm, color: colors.textSecondary, textAlign: 'center', marginTop: spacing.xl },
  card: {
    backgroundColor: colors.surface, borderRadius: borderRadius.lg,
    borderWidth: 1, borderColor: colors.border,
    padding: spacing.md, marginBottom: spacing.md,
  },
  cardWarn: { borderColor: colors.danger },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  customer: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.text },
  amount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.text },
  meta: { fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 },
  errorBox: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs,
    marginTop: spacing.sm, padding: spacing.sm,
    backgroundColor: colors.surfaceSecondary, borderRadius: borderRadius.md,
  },
  errorText: { flex: 1, fontSize: fontSize.xs, color: colors.danger },
  settleBtn: { marginTop: spacing.sm },
});
